/**
 * WidgetWriteGuard — validation, repair, and merge guards for ALL widget types.
 *
 * Public API
 * ──────────
 *  validateWidget(card)          Generic validator for any widget type.
 *  validateEventCard(card)       Backward-compat alias (Event-specific).
 *  isCardEmpty(card)             True when ALL required fields are blank.
 *  wouldOverwriteValidCard(e,n)  True when incoming data would destroy existing valid data.
 *  safeEventCardMerge(e,n)       Merge incoming Event update without blanking fields.
 *  repairWidget(card)            Structural normalisation — fixes types/arrays, never fakes data.
 *
 * Severity levels
 * ───────────────
 *  ok       — all required fields present, structure correct.
 *  low      — minor structural issues only (missing optional arrays).
 *  medium   — some required fields blank but at least one present.
 *  critical — ALL required fields blank — widget is completely empty.
 *
 * Repair vs. data entry
 * ──────────────────────
 *  repairWidget does STRUCTURAL normalisation only:
 *    ✅ Adds missing arrays (going:[], images:[], answers:[] …)
 *    ✅ Corrects wrong types (null→[], string→'' , undefined removed)
 *    ✅ Ensures required keys exist with zero-value defaults
 *    ❌ Never invents text content — blank title stays blank
 *  After repair, severity may still be medium/critical if text fields are empty.
 *  Those require manual edit by the user.
 */

// ─── helpers ─────────────────────────────────────────────────────────────────

export function str(val) {
  if (val === null || val === undefined) return '';
  return String(val).trim();
}

function arr(val) {
  return Array.isArray(val) ? val : [];
}

// ─── schema registry ──────────────────────────────────────────────────────────

/**
 * WIDGET_SCHEMAS defines the expected shape for each widget type.
 *
 * Each entry has:
 *   requiredTextFields — at least one must be non-empty for severity < critical
 *   coreTextField      — THE single field that makes a widget "valid" (title-equivalent)
 *   arrayFields        — fields that must be arrays (default [])
 *   defaults           — zero-value defaults for each structural field
 *   getLabel(card)     — human readable label for UI display
 */
export const WIDGET_SCHEMAS = {
  Event: {
    coreTextField: 'eventTitle',
    requiredTextFields: ['eventTitle', 'eventDate'],
    arrayFields: ['going', 'maybe', 'decline', 'eventPhotos', 'events'],
    defaults: {
      id: '', type: 'Event', content: '', position: 0, metadata: {}, events: [],
      eventPhotos: [], eventId: null, eventTitle: '', eventDate: '', eventEndDate: '',
      eventLocation: '', eventLocationCoords: null, locationCoords: null,
      eventDescription: '', eventUrl: '', rsvpEnabled: false,
      reminderSettings: { enabled: false, reminderTimes: [] },
      going: [], maybe: [], decline: [],
    },
    getLabel: (c) => c.eventTitle || '(no title)',
  },
  TextBox: {
    coreTextField: null, // content is intentionally optional
    requiredTextFields: [],
    arrayFields: [],
    defaults: { id: '', type: 'TextBox', content: '', position: 0 },
    getLabel: (c) => (c.content || '').slice(0, 40) || '(empty)',
  },
  ImageGallery: {
    coreTextField: null,
    requiredTextFields: [],
    arrayFields: ['images'],
    contentArrayFields: ['images', 'eventPhotos'], // at least one must have entries
    defaults: { id: '', type: 'ImageGallery', images: [], position: 0 },
    getLabel: () => 'Image Gallery',
  },
  PictureFrame: {
    coreTextField: null,
    requiredTextFields: [],
    arrayFields: ['images'],
    contentArrayFields: ['images'], // must have entries
    defaults: { id: '', type: 'PictureFrame', images: [], position: 0 },
    getLabel: () => 'Picture Frame',
  },
  Countdown: {
    coreTextField: 'targetDate',
    requiredTextFields: ['targetDate'],
    arrayFields: [],
    defaults: { id: '', type: 'Countdown', targetDate: '', onCompleteMessage: '', position: 0 },
    getLabel: (c) => c.targetDate ? `Countdown → ${c.targetDate}` : '(no target date)',
  },
  Poll: {
    coreTextField: 'question',
    requiredTextFields: ['question'],
    arrayFields: ['answers', 'votes'],
    defaults: { id: '', type: 'Poll', question: '', answers: [], votes: [], position: 0 },
    getLabel: (c) => c.question || '(no question)',
  },
  URLPreview: {
    coreTextField: 'url',
    requiredTextFields: ['url'],
    arrayFields: [],
    defaults: { id: '', type: 'URLPreview', url: '', customImageUrl: '', position: 0 },
    getLabel: (c) => c.url || '(no URL)',
  },
  RSVP: {
    coreTextField: null,
    requiredTextFields: [],
    arrayFields: ['going', 'maybe', 'decline'],
    defaults: { id: '', type: 'RSVP', going: [], maybe: [], decline: [], position: 0 },
    getLabel: () => 'RSVP Widget',
  },
};

const UNKNOWN_SCHEMA = {
  coreTextField: null,
  requiredTextFields: [],
  arrayFields: [],
  defaults: {},
  getLabel: (c) => `Unknown (${c.type || '?'})`,
};

function schemaFor(card) {
  return WIDGET_SCHEMAS[card?.type] || UNKNOWN_SCHEMA;
}

// ─── public API ──────────────────────────────────────────────────────────────

/**
 * Validate ANY widget card.
 *
 * @param {Object} card
 * @returns {{ valid: boolean, severity: 'ok'|'low'|'medium'|'critical', errors: string[], type: string }}
 */
export function validateWidget(card) {
  if (!card || typeof card !== 'object') {
    return { valid: false, severity: 'critical', errors: ['Card is null or not an object.'], type: '?' };
  }

  // A widget with no type will never render — treat it as critical immediately.
  if (!card.type || !String(card.type).trim()) {
    return { valid: false, severity: 'critical', errors: ['Widget type is empty — widget will not render on the page.'], type: '' };
  }

  const schema = schemaFor(card);
  const errors = [];
  let structuralOnly = true;

  // Check array fields exist and are arrays
  for (const field of schema.arrayFields) {
    if (!Array.isArray(card[field])) {
      errors.push(`${field} is not an array (got: ${typeof card[field]})`);
    }
  }

  // Check required text fields
  const presentRequired = [];
  for (const field of schema.requiredTextFields) {
    if (!str(card[field])) {
      errors.push(`${field} is empty`);
    } else {
      presentRequired.push(field);
      structuralOnly = false;
    }
  }

  if (schema.requiredTextFields.length === 0) {
    // Type has no required text (TextBox, ImageGallery, RSVP, PictureFrame)
    const structuralErrors = errors.filter(e => e.includes('not an array'));
    if (structuralErrors.length > 0) return { valid: true, severity: 'low', errors, type: card.type };
    // TextBox must have non-empty content — an empty textbox is never shown to visitors
    if (card.type === 'TextBox') {
      const hasContent = String(card.content || card.text || '').trim().length > 0;
      if (!hasContent) {
        return { valid: false, severity: 'medium', errors: ['TextBox has no content — widget will be blank for visitors.'], type: card.type };
      }
    }
    // Check content arrays — if all empty, widget renders blank for visitors
    if (schema.contentArrayFields?.length > 0) {
      const hasContent = schema.contentArrayFields.some(
        f => Array.isArray(card[f]) && card[f].length > 0
      );
      if (!hasContent) {
        return { valid: false, severity: 'medium', errors: ['No images added yet — widget will be blank for visitors.'], type: card.type };
      }
    }
    return { valid: true, severity: 'ok', errors: [], type: card.type };
  }

  if (presentRequired.length === 0) {
    // All required text fields empty → critical
    return { valid: false, severity: 'critical', errors, type: card.type };
  }

  if (presentRequired.length < schema.requiredTextFields.length) {
    // Some required text fields missing → medium
    return { valid: false, severity: 'medium', errors, type: card.type };
  }

  // All required text fields present
  const structErrors = errors.filter(e => e.includes('not an array'));
  if (structErrors.length > 0) return { valid: true, severity: 'low', errors, type: card.type };
  return { valid: true, severity: 'ok', errors: [], type: card.type };
}

/**
 * Backward-compat alias used by EventsWidget.js and EventCard.js write paths.
 * Returns the same shape as the old validateEventCard.
 */
export function validateEventCard(card) {
  const result = validateWidget(card?.type === 'Event' ? card : { ...card, type: 'Event' });
  // Map critical→empty, medium→partial for backward compat
  const severityMap = { critical: 'empty', medium: 'partial', low: 'ok', ok: 'ok' };
  return {
    valid: result.valid,
    severity: severityMap[result.severity] || result.severity,
    errors: result.errors,
  };
}

/** True when ALL required fields are blank (severity === critical) or the card is an empty TextBox. */
export function isCardEmpty(card) {
  if (card?.type === 'TextBox') {
    return !String(card.content || card.text || '').trim();
  }
  return validateWidget(card).severity === 'critical';
}

/**
 * True when a card is safe to permanently delete — ALL meaningful fields are empty.
 *
 * Stricter than `isCardEmpty`: also checks non-text fields (photos, RSVP respondents,
 * location coordinates, rsvpEnabled flag, metadata) before allowing any deletion.
 * A card with RSVP respondents or photos but no title is NOT truly empty.
 *
 * Use this before any soft-delete or bulk-archive operation to prevent data loss.
 *
 * @param {Object} card  - Widget card object (any type).
 * @returns {boolean}    - true only when every meaningful field is blank/empty.
 */
export function isTrulyEmpty(card) {
  if (!card || typeof card !== 'object') return true;

  // Must first pass the basic empty check (all required text fields blank)
  if (!isCardEmpty(card)) return false;

  // Event-specific: check non-text fields that may hold real data
  if (card.type === 'Event' || !card.type) {
    const s = v => (v == null ? '' : String(v).trim());
    const a = v => (Array.isArray(v) ? v : []);

    // Optional text fields that carry meaningful data
    if (s(card.eventLocation)) return false;
    if (s(card.eventUrl)) return false;

    // RSVP respondent arrays
    if (a(card.going).length > 0) return false;
    if (a(card.maybe).length > 0) return false;
    if (a(card.decline).length > 0) return false;

    // Photo arrays
    if (a(card.eventPhotos).length > 0) return false;

    // Location coordinates
    if (card.eventLocationCoords != null) return false;
    if (card.locationCoords != null) return false;

    // RSVP explicitly enabled
    if (card.rsvpEnabled === true) return false;

    // Non-empty metadata object
    if (card.metadata && typeof card.metadata === 'object' && Object.keys(card.metadata).length > 0) return false;

    // Active reminder settings
    if (card.reminderSettings?.enabled === true) return false;
    if (Array.isArray(card.reminderSettings?.reminderTimes) && card.reminderSettings.reminderTimes.length > 0) return false;
  }

  return true;
}

/**
 * Structurally repair a widget — normalise arrays, correct types, remove undefined.
 *
 * This function NEVER invents user data. Blank required text fields remain blank
 * after repair (they need manual edit). What repair DOES fix:
 *   - Arrays that should exist but don't (null / undefined / wrong type)
 *   - Missing structural keys present in schema defaults
 *   - Objects that should be plain objects (reminderSettings, metadata)
 *   - Removes undefined values that break Firestore writes
 *
 * Returns the normalised card and a list of changes made.
 *
 * @param {Object} card
 * @returns {{ repaired: Object, changes: string[] }}
 */
export function repairWidget(card) {
  if (!card || typeof card !== 'object') {
    return { repaired: { id: String(Date.now()), type: 'TextBox', content: '', position: 0 }, changes: ['Card was null — built minimal skeleton.'] };
  }

  const schema = schemaFor(card);
  const changes = [];
  const result = { ...schema.defaults };

  // Overlay existing values — but only those that are defined
  for (const [key, value] of Object.entries(card)) {
    if (value !== undefined) result[key] = value;
  }

  // Ensure id is a string
  if (!result.id || typeof result.id !== 'string') {
    result.id = `widget_fixed_${Date.now()}`;
    changes.push('id was missing — generated new id');
  }

  // Ensure type matches
  if (!result.type) {
    result.type = card.type || 'TextBox';
    changes.push('type was missing — set to ' + result.type);
  }

  // Ensure position is a number
  if (typeof result.position !== 'number') {
    result.position = 0;
    changes.push('position was not a number — reset to 0');
  }

  // Fix array fields
  for (const field of schema.arrayFields) {
    if (!Array.isArray(result[field])) {
      const old = result[field];
      result[field] = [];
      changes.push(`${field}: was ${old === null ? 'null' : typeof old} → set to []`);
    }
  }

  // Event-specific: fix reminderSettings shape
  if (card.type === 'Event') {
    if (!result.reminderSettings || typeof result.reminderSettings !== 'object' || Array.isArray(result.reminderSettings)) {
      result.reminderSettings = { enabled: false, reminderTimes: [] };
      changes.push('reminderSettings: was invalid type → reset to default object');
    }
    if (!Array.isArray(result.reminderSettings.reminderTimes)) {
      result.reminderSettings = { ...result.reminderSettings, reminderTimes: [] };
      changes.push('reminderSettings.reminderTimes: was not an array → set to []');
    }
    if (typeof result.rsvpEnabled !== 'boolean') {
      result.rsvpEnabled = false;
      changes.push('rsvpEnabled: was not boolean → set to false');
    }
  }

  // Remove undefined values throughout
  const cleaned = removeUndefined(result);
  if (Object.keys(cleaned).length < Object.keys(result).length) {
    changes.push('removed undefined fields from card');
  }

  return { repaired: cleaned, changes };
}

function removeUndefined(obj) {
  if (Array.isArray(obj)) return obj.map(removeUndefined);
  if (obj && typeof obj === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v !== undefined) out[k] = removeUndefined(v);
    }
    return out;
  }
  return obj;
}

/**
 * Returns true when the incoming `newCard` should NOT overwrite `existingCard`.
 * Blocks write if new card is invalid AND existing card is valid.
 */
export function wouldOverwriteValidCard(existingCard, newCard) {
  if (!existingCard) return false;
  const existingV = validateWidget(existingCard);
  const newV = validateWidget(newCard);
  return !newV.valid && existingV.valid;
}

/**
 * Merge an incoming Event card update safely — prefers non-empty incoming text,
 * always preserves participation arrays from Firestore.
 */
export function safeEventCardMerge(existingCard, incomingCard) {
  if (!existingCard) return incomingCard;

  const preserved = {
    id: existingCard.id,
    type: 'Event',
    position: incomingCard.position ?? existingCard.position,
    going: arr(existingCard.going),
    maybe: arr(existingCard.maybe),
    decline: arr(existingCard.decline),
    eventPhotos: arr(existingCard.eventPhotos),
  };

  const textFields = ['eventTitle', 'eventDate', 'eventEndDate', 'eventLocation', 'eventDescription', 'eventUrl'];
  const textMerged = {};
  for (const field of textFields) {
    const incoming = str(incomingCard[field]);
    const existing = str(existingCard[field]);
    textMerged[field] = incoming || existing || '';
  }

  const coordFields = {
    eventLocationCoords: incomingCard.eventLocationCoords ?? existingCard.eventLocationCoords ?? null,
    locationCoords: incomingCard.locationCoords ?? existingCard.locationCoords ?? null,
    eventId: incomingCard.eventId ?? existingCard.eventId ?? null,
  };

  const scalarState = {
    rsvpEnabled: typeof incomingCard.rsvpEnabled === 'boolean'
      ? incomingCard.rsvpEnabled
      : (existingCard.rsvpEnabled || false),
    reminderSettings: (incomingCard.reminderSettings?.enabled || incomingCard.reminderSettings?.reminderTimes?.length)
      ? incomingCard.reminderSettings
      : (existingCard.reminderSettings || { enabled: false, reminderTimes: [] }),
  };

  return { ...existingCard, ...preserved, ...textMerged, ...coordFields, ...scalarState };
}


